import styled from 'styled-components';

import { Box } from '@hivebrite/design-system/Box';

import { HeadlineLink } from 'hb-builder/components/BuilderComponents/page/block/Strips/components/HeadlineLink';
import type { TemplateWithButtonFrame } from 'hb-builder/components/BuilderComponents/page/block/Strips/types/items';
import { Children } from 'hb-builder/components/common/Item';
import type { RenderProps } from 'hb-builder/components/common/type/RenderProps';
import { ChildrenRenderer } from 'hb-builder/components/Renderer/ChildrenRenderer';
import {
  useBuilderConfigContext,
  useComponentContext,
  useRendererContext,
} from 'hb-builder/hooks';
import type { BuilderComponent } from 'hb-builder/types';

import DumbRoundedImage from './DumbRoundedImage';

const TemplateWithLongTextStripsItem: BuilderComponent = () => <div />; // never used

const RenderBuilder = () => {
  const { item } = useComponentContext();
  const {
    hivebrite: { subdomainNetworkUrl },
  } = useBuilderConfigContext();

  const [image, headline, subline, button] =
    item.children as TemplateWithButtonFrame['children'];

  const { attributes: buttonAttributes } = button;
  const { attributes: buttonImage } = image;

  const { buttonLink: link } = buttonAttributes || {};
  const { url, clickable } = buttonImage || {};

  const canClickOnHeadline = !!(link && clickable);

  return (
    <Wrapper dataTestId="template-long-text-strips-item-wrapper">
      {url ? (
        <DumbRoundedImage
          hasLink={canClickOnHeadline}
          isBackoffice
          image={image}
          subdomainNetworkUrl={subdomainNetworkUrl}
        />
      ) : (
        <Box />
      )}
      <StyledBox>
        {canClickOnHeadline ? (
          <HeadlineLink isBackoffice>
            <Children item={{ children: [headline] }} />
          </HeadlineLink>
        ) : (
          <Box>
            <Children item={{ children: [headline] }} />
          </Box>
        )}
        <Children item={{ children: [subline, button] }} />
      </StyledBox>
    </Wrapper>
  );
};

const Render = ({ item }: RenderProps<TemplateWithButtonFrame>) => {
  const { subdomainNetworkUrl } = useRendererContext();
  const [image, headline, subline, button] = item?.children || [];

  const { clickable, url } = image?.attributes || {};

  const { buttonLink: link, buttonLinkNewPage: linkNewPage } =
    button?.attributes || {};

  const canClickOnHeadline = link && clickable;

  const imageProps = {
    fallBackAltValue: headline?.content,
    hasLink: clickable,
    image,
    link,
    linkNewPage,
    subdomainNetworkUrl,
  };

  return (
    <Wrapper dataTestId="template-long-text-strips-item-wrapper">
      {url ? <DumbRoundedImage {...imageProps} /> : <Box />}
      <StyledBox>
        {canClickOnHeadline ? (
          <HeadlineLink href={link} target={linkNewPage ? '_blank' : '_self'}>
            <ChildrenRenderer item={{ children: [headline] }} />
          </HeadlineLink>
        ) : (
          <Box>
            <ChildrenRenderer item={{ children: [headline] }} />
          </Box>
        )}
        <ChildrenRenderer item={{ children: [subline, button] }} />
      </StyledBox>
    </Wrapper>
  );
};

TemplateWithLongTextStripsItem.item = ({ children, template }) => ({
  children,
  template,
  type: TemplateWithLongTextStripsItem.componentName,
});

TemplateWithLongTextStripsItem.componentName = 'TemplateWithLongTextStripsItem';
TemplateWithLongTextStripsItem.componentType = 'single';

TemplateWithLongTextStripsItem.canBeDoubleClicked = false;
TemplateWithLongTextStripsItem.canBeDragged = false;
TemplateWithLongTextStripsItem.canBeListed = false;
TemplateWithLongTextStripsItem.canBeSelected = false;
TemplateWithLongTextStripsItem.componentClickable = false;

TemplateWithLongTextStripsItem.RenderBuilder = RenderBuilder;
TemplateWithLongTextStripsItem.Render = Render;

export default TemplateWithLongTextStripsItem;

const StyledBox = styled(Box).attrs(() => ({
  pl: 6,
  width: '100%',
  flexDirection: 'column',
  justifyContent: 'flex-start',
  height: '100%',
}))`
  border-left: 1px solid black;
`;

const Wrapper = styled(Box).attrs(() => ({
  width: '100%',
  flexDirection: 'column',
  justifyContent: 'space-between',
  height: '100%',
}))``;
